import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Star, Quote } from "lucide-react";

const ReviewsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reviews = [
    {
      name: "Anna & Erik Lindström",
      title: "Villaägare, Djursholm",
      text: "Otroligt kunniga och professionella. De förvandlade vår villa med fantastisk ljusdesign. Helt klart en annan nivå än vanliga elektriker.",
      rating: 5,
    },
    {
      name: "Johan Bergman",
      title: "Arkitekt, Stockholm",
      text: "Vi har anlitat Optimal EL i över 10 år för våra projekt. De förstår design och levererar alltid med högsta kvalitet. Oumbärliga samarbetspartners.",
      rating: 5,
    },
    {
      name: "Maria Sjöberg",
      title: "Fastighetsägare, Östermalm",
      text: "Väldigt tydliga offerter och hög kvalitet på utfört arbete. De höll tiden perfekt och städade efter sig. Rekommenderar varmt!",
      rating: 5,
    },
    {
      name: "Byggbolaget Strand AB",
      title: "Byggföretag, Stockholm",
      text: "Pålitlig UE-partner som alltid levererar. Deras dokumentation är exemplarisk och de löser alltid uppkomna problem snabbt och professionellt.",
      rating: 5,
    },
    {
      name: "Henrik & Sara Norén",
      title: "Villaägare, Bromma",
      text: "Installerade smart belysning i hela huset. Fantastiskt resultat och de tog sig tid att förklara systemet ordentligt. Mycket nöjda!",
      rating: 5,
    },
  ];

  return (
    <section
      id="omdomen"
      data-testid="reviews-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#050505] overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
            >
              Omdömen
            </motion.p>

            <motion.h2
              data-testid="reviews-title"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-manrope font-light text-white tracking-tight"
            >
              Vad våra kunder
              <br />
              <span className="text-zinc-500">säger om oss.</span>
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 md:mt-0 flex items-center gap-3"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <span className="text-white font-manrope text-2xl font-light">4,9</span>
            <span className="text-zinc-500 text-sm">i betyg</span>
          </motion.div>
        </div>
      </div>

      {/* Horizontal Scrolling Reviews */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="horizontal-scroll flex gap-4 md:gap-6 px-4 md:px-12 pb-4"
        data-testid="reviews-scroll-container"
      >
        {reviews.map((review, index) => (
          <motion.div
            key={index}
            data-testid={`review-card-${index}`}
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
            className="scroll-snap-item flex-shrink-0 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/5 p-5 md:p-8 w-[280px] md:w-[380px]"
          >
            {/* Quote Icon */}
            <Quote className="w-6 h-6 md:w-8 md:h-8 text-[#D4AF37]/30 mb-3 md:mb-4" />

            {/* Review Text */}
            <p className="text-zinc-300 text-sm md:text-base leading-relaxed mb-4 md:mb-6">
              "{review.text}"
            </p>

            {/* Rating */}
            <div className="flex gap-1 mb-3 md:mb-4">
              {[...Array(review.rating)].map((_, i) => (
                <Star key={i} className="w-3 h-3 md:w-4 md:h-4 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>

            {/* Author */}
            <div>
              <div className="text-white font-medium text-sm md:text-base">{review.name}</div>
              <div className="text-zinc-500 text-xs md:text-sm">{review.title}</div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};

export default ReviewsSection;
